import {Component, Inject} from '@angular/core';
import {FormBuilder, FormControl, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ScheduleItem} from "../ScheduleItem";
import moment from "moment";

@Component({
  selector: 'app-schedule-view',
  templateUrl: './schedule-view.component.html',
  styleUrls: ['./schedule-view.component.css']
})
export class ScheduleViewComponent {

  startControl: FormControl;
  endControl: FormControl;

  relativeStart: string;
  relativeEnd: string;

  delayed: boolean;
  unixStart: number;
  unixEnd: number;

  types: string[] = ['PANEL', 'MEET', 'EVENT', 'GAME', 'STREAM']
  locations: string[] = ['MAIN', 'COMMUNITY', 'OUTDOOR', 'MEET_AREA', 'VENUE', 'STICKYPISTON', 'ON_STREAM']

  convert = (date: Date) => new Date(date.getTime() + new Date().getTimezoneOffset() * -60 * 1000).toISOString().slice(0, 19)

  constructor(private _formBuilder: FormBuilder,
              public dialogRef: MatDialogRef<ScheduleViewComponent>,
              @Inject(MAT_DIALOG_DATA) public item: ScheduleItem) {
    this.startControl = new FormControl(undefined, {validators: [Validators.required]});
    this.endControl = new FormControl(undefined, {validators: [Validators.required]});

    this.startControl.setValue(item.delayedStart !== undefined ?
      this.convert(item.delayedStart) : this.convert(item.starts));
    this.endControl.setValue(item.delayedEnd !== undefined ?
      this.convert(item.delayedEnd) : this.convert(item.ends))
    this.delayed = item.delayedStart !== undefined;
    this.unixStart = Math.floor(new Date(this.startControl.value).getTime() / 1000);
    this.unixEnd = Math.floor(new Date(this.endControl.value).getTime() / 1000);

    this.relativeStart = moment(this.startControl.value).fromNow();
    this.relativeEnd = moment(this.endControl.value).fromNow();

    this.startControl.valueChanges.subscribe((a) => {
      this.unixStart = Math.floor(new Date(this.startControl.value).getTime() / 1000);
      this.relativeStart = moment(this.startControl.value).fromNow();
      this.relativeEnd = moment(this.endControl.value).fromNow();
    })

    this.endControl.valueChanges.subscribe((a) => {
      this.unixEnd = Math.floor(new Date(this.endControl.value).getTime() / 1000);
      this.relativeStart = moment(this.startControl.value).fromNow();
      this.relativeEnd = moment(this.endControl.value).fromNow();
    })
  }

  save() {
    // Basic validation: require both dates and start before end
    if (!this.startControl.value || !this.endControl.value) {
      return;
    }
    // Optional: require a name
    if (!this.item.name || String(this.item.name).trim().length === 0) {
      return;
    }
    const startDate = new Date(this.startControl.value);
    const endDate = new Date(this.endControl.value);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || startDate.getTime() >= endDate.getTime()) {
      return;
    }
    this.dialogRef.close({
      name: this.item.name,
      delayed: this.delayed,
      starts: this.delayed ? this.item.starts.getTime() : startDate.getTime(),
      ends: this.delayed ? this.item.ends.getTime() : endDate.getTime(),
      delayedStart: this.delayed ? startDate.getTime() : undefined,
      delayedEnd: this.delayed ? endDate.getTime() : undefined,
      type: this.item.type,
      location: this.item.location,
      cancelled: this.item.cancelled,
      delete: false
    })
  }

  setFromUnix(which: 'start' | 'end') {
    const value = Number(which === 'start' ? this.unixStart : this.unixEnd);
    if (!Number.isFinite(value)) return;
    const date = new Date(value < 100000000000 ? value * 1000 : value);
    if (Number.isNaN(date.getTime())) return;
    (which === 'start' ? this.startControl : this.endControl).setValue(this.convert(date));
  }

  startNow() {
    const now = new Date();
    const ends = new Date(this.endControl.value ?? now);
    // If current end is before now, push end to now + 30 minutes
    if (isNaN(ends.getTime()) || ends.getTime() <= now.getTime()) {
      ends.setTime(now.getTime() + 30 * 60 * 1000);
    }
    this.delayed = true;
    this.startControl.setValue(this.convert(now));
    this.endControl.setValue(this.convert(ends));
  }

  pushBack(minutes: number) {
    const start = new Date(this.startControl.value ?? new Date());
    const end = new Date(this.endControl.value ?? new Date(start.getTime() + 30 * 60 * 1000));
    if (!isNaN(start.getTime())) start.setTime(start.getTime() + minutes * 60 * 1000);
    if (!isNaN(end.getTime())) end.setTime(end.getTime() + minutes * 60 * 1000);
    this.delayed = true;
    this.startControl.setValue(this.convert(start));
    this.endControl.setValue(this.convert(end));
  }

  remove() {
    if (this.item.id === 0 || window.confirm('Remove this schedule entry?'))
      this.dialogRef.close({delete: true});
  }

  canSave(): boolean {
    if (!this.startControl.value || !this.endControl.value) return false;
    if (!this.item.name || String(this.item.name).trim().length === 0) return false;
    const startDate = new Date(this.startControl.value);
    const endDate = new Date(this.endControl.value);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;
    return startDate.getTime() < endDate.getTime();
  }
}
